import React, { useState, useEffect, useRef } from 'react';
import { useUsers } from '@/hooks/useShared';
import { finalizeCashDistribution } from '@/hooks/useSettlement';
import { useAuth } from '@/hooks/useAuth';
import { formatCurrency } from '@/lib/utils';
import { canManageUsers } from '@/lib/roles';
import { calculateSmartDistribution } from '@/lib/settlement';
import type { Game } from '@/lib/types';

interface CashDistributionStepProps {
  game: Game;
  onNext: () => void;
  onBack: () => void;
}

function cashDraftKey(gameId: string): string {
  return `pk:settlement:cash-draft:${gameId}`;
}

function readCashDraft(gameId: string): Record<string, string> | null {
  if (typeof window === 'undefined') return null;
  const raw = window.sessionStorage.getItem(cashDraftKey(gameId));
  if (!raw) return null;
  try {
    return JSON.parse(raw) as Record<string, string>;
  } catch {
    window.sessionStorage.removeItem(cashDraftKey(gameId));
    return null;
  }
}

function writeCashDraft(gameId: string, cash: Record<string, string>): void {
  if (typeof window === 'undefined') return;
  window.sessionStorage.setItem(cashDraftKey(gameId), JSON.stringify(cash));
}

function clearCashDraft(gameId: string): void {
  if (typeof window === 'undefined') return;
  window.sessionStorage.removeItem(cashDraftKey(gameId));
}

function buildInitialCash(game: Game): Record<string, string> {
  const activePlayers = game.players.filter(p => !p.is_spectator);
  const draft = readCashDraft(game.id);

  // If we have a draft, merge it with persisted DB values (DB wins for finalized players).
  if (draft) {
    const playerIds = new Set(activePlayers.map(p => p.user_id));
    const merged: Record<string, string> = {};
    activePlayers.forEach(p => {
      const cash_received = game.settlement?.player_inputs[p.user_id]?.cash_received;
      if (cash_received !== null && cash_received !== undefined) {
        merged[p.user_id] = cash_received.toString();
      } else if (playerIds.has(p.user_id) && draft[p.user_id] !== undefined) {
        merged[p.user_id] = draft[p.user_id];
      } else {
        merged[p.user_id] = "";
      }
    });
    return merged;
  }

  const hasPersistedCash = activePlayers.some(p => {
    const cash_received = game.settlement?.player_inputs[p.user_id]?.cash_received;
    return cash_received !== null && cash_received !== undefined;
  });
  const totalCashPot = game.settlement?.player_snapshot.reduce((sum, p) => sum + (p.cashIn || 0), 0) || 0;

  if (!hasPersistedCash && totalCashPot > 0) {
    const smartPlayers = activePlayers.map(p => ({
      user_id: p.user_id,
      cash_in: p.cash_in || 0,
      total_bought_in: p.total_bought_in || 0,
      final_stack: game.settlement?.player_inputs[p.user_id]?.final_stack || 0,
    }));
    const smartDistribution = calculateSmartDistribution(smartPlayers, totalCashPot);
    return Object.fromEntries(
      Object.entries(smartDistribution).map(([userId, amount]) => [userId, amount > 0 ? amount.toString() : ""]),
    );
  }

  const initialCash: Record<string, string> = {};
  activePlayers.forEach(p => {
    const final_stack = game.settlement?.player_inputs[p.user_id]?.final_stack || 0;
    const autoValue = Math.floor(Math.min(final_stack, p.cash_in || 0) / 5) * 5;
    const cash_received = game.settlement?.player_inputs[p.user_id]?.cash_received;
    if (cash_received !== null && cash_received !== undefined) {
      initialCash[p.user_id] = cash_received.toString();
    } else {
      initialCash[p.user_id] = autoValue > 0 ? autoValue.toString() : "";
    }
  });
  return initialCash;
}



export function CashDistributionStep({ game, onNext, onBack }: CashDistributionStepProps) {
  const { user } = useAuth();
  const { users } = useUsers();
  const isAdmin = canManageUsers(user);
  const [localCash, setLocalCash] = useState<Record<string, string>>(() => buildInitialCash(game));
  const [isSyncing, setIsSyncing] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const localCashDirtyRef = useRef(false);
  const initializedGameIdRef = useRef<string | null>(game.id);

  useEffect(() => {
    const isNewGame = initializedGameIdRef.current !== game.id;
    if (!isNewGame && localCashDirtyRef.current) {
      return;
    }

    setLocalCash(buildInitialCash(game));
    initializedGameIdRef.current = game.id;
    localCashDirtyRef.current = false;
  }, [game.id, game.players, game.settlement?.player_inputs]);

  // Persist cash distribution draft to sessionStorage.
  useEffect(() => {
    if (!game?.id) return;
    writeCashDraft(game.id, localCash);
  }, [localCash, game?.id]);

  const cashPot = game.settlement?.player_snapshot.reduce((sum, p) => sum + (p.cashIn || 0), 0) || 0;
  const totalDistributed = Object.values(localCash).reduce((sum, val) => sum + (parseFloat(val) || 0), 0);
  const remainingCash = cashPot - totalDistributed;
  const isCashValid = Math.abs(remainingCash) < 0.01;

  const handleClearAll = () => {
    const cleared: Record<string, string> = {};
    game.players.forEach(p => { cleared[p.user_id] = ""; });
    localCashDirtyRef.current = true;
    setLocalCash(cleared);
    if (game?.id) clearCashDraft(game.id);
  };

  const handleCashChange = (playerUserId: string, value: string) => {
    localCashDirtyRef.current = true;
    setLocalCash(prev => ({ ...prev, [playerUserId]: value }));
  };

  const handleNext = async () => {
    setIsSyncing(true);
    try {
      // Convert localCash Record<string, string> to Record<string, number>
      const cashUpdates: Record<string, number> = {};
      Object.entries(localCash).forEach(([userId, value]) => {
        cashUpdates[userId] = parseFloat(value) || 0;
      });
      
      await finalizeCashDistribution(game.id, user?.id ?? '', cashUpdates);
      clearCashDraft(game.id);
      onNext();
    } finally {
      setIsSyncing(false);
    }
  };

  const handleScroll = (playerUserId: string, delta: number) => {
    const current = parseFloat(localCash[playerUserId]) || 0;
    const next = Math.max(0, current + (delta * 5));
    handleCashChange(playerUserId, next.toString());
  };

  const allPlayers = game.players
    .filter(p => !p.is_spectator)
    .map(p => {
      const userData = users.find(u => u.id === p.user_id);
      return {
        ...p,
        name: userData?.name || p.name || 'Unknown'
      };
    })
    .sort((a, b) => {
      // Prioritize players who put in cash
      if ((b.cash_in || 0) !== (a.cash_in || 0)) {
        return (b.cash_in || 0) - (a.cash_in || 0);
      }
       // Then sort by net profit
       const bNet = (game.settlement?.player_inputs[b.user_id]?.final_stack || 0) - (b.total_bought_in || 0);
       const aNet = (game.settlement?.player_inputs[a.user_id]?.final_stack || 0) - (a.total_bought_in || 0);
       return bNet - aNet;

    });

  const handleSmartDistribute = () => {
    const totalCashPot = game.settlement?.player_snapshot.reduce((sum, p) => sum + (p.cashIn || 0), 0) || 0;
     const smartPlayers = allPlayers.map(p => ({
       user_id: p.user_id,
       cash_in: p.cash_in || 0,
       total_bought_in: p.total_bought_in || 0,
       final_stack: game.settlement?.player_inputs[p.user_id]?.final_stack || 0,
     }));
    const result = calculateSmartDistribution(smartPlayers, totalCashPot);
    
    const newCash: Record<string, string> = {};
    Object.entries(result).forEach(([userId, amount]) => {
      newCash[userId] = amount.toString();
    });
    
    localCashDirtyRef.current = true;
    setLocalCash(newCash);
  };


   const visiblePlayers = showAll ? allPlayers : allPlayers.filter(p => {
     const net = (game.settlement?.player_inputs[p.user_id]?.final_stack || 0) - (p.total_bought_in || 0);
     const claim = net + (p.cash_in || 0);
     return claim > 0.01;
   });


  return (
    <div>
      <div className="mb-2 mt-1 flex justify-between items-end">
        <div className="flex gap-3">
          <button 
            onClick={handleSmartDistribute}
            className="bg-white border border-[#e5e9ea] text-[#595c5d] px-2 py-1 text-[9px] font-extrabold uppercase tracking-wider rounded-[10px] cursor-pointer"
          >
            Smart Dist
          </button>
          <button 
            onClick={handleClearAll}
            className="bg-white border border-[#e5e9ea] text-[#595c5d] px-2 py-1 text-[9px] font-extrabold uppercase tracking-wider rounded-[10px] cursor-pointer"
          >
            Clear All
          </button>
        </div>
      </div>

      {visiblePlayers.map((player) => {
        const initial = (player.name || 'P').charAt(0).toUpperCase();
        return (
          <div key={player.user_id} className="bg-white rounded-xl p-[12px_14px] mb-1.5 flex items-center border border-[#e5e9ea]">
            <div className="w-9 h-9 rounded-full bg-[#006855] flex items-center justify-center mr-3 text-[15px] font-extrabold text-white shrink-0">{initial}</div>
              <div className="flex-1">
                <div className="text-sm font-semibold text-[#2c2f30]">
                  {player.name}
                  {player.left_early && (
                    <span className="ml-2 text-[10px] font-bold uppercase text-[#b31b25] bg-[#b31b25]/10 px-1.5 py-0.5 rounded">Left Early</span>
                  )}
                </div>
                     <div className="text-[#006855] text-[11px] font-bold mt-0.25">
                       Cash: {formatCurrency(player.cash_in || 0)} Stack: {formatCurrency(game.settlement?.player_inputs[player.user_id]?.final_stack || 0)}
                     </div>
              </div>

            <div className="ml-auto flex items-center gap-1.5">
              <button 
                onClick={() => handleScroll(player.user_id, -1)}
                className="bg-white text-[#595c5d] border border-[#e5e9ea] font-bold cursor-pointer w-8 h-8 p-0 m-0 text-[18px] flex items-center justify-center rounded-lg"
              >
                -
              </button>
              <div className="relative">
                <span className="text-sm font-bold text-[#595c5d] absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none opacity-50">$</span>
                <input
                  type="number"
                  inputMode="decimal"
                  placeholder="0.00"
                  value={localCash[player.user_id] ?? ''}
                  onChange={(e) => handleCashChange(player.user_id, e.target.value)}
                  className="w-[85px] bg-[#f5f7f8] border border-[#e5e9ea] rounded-lg pr-2.5 py-2 text-[15px] font-bold text-[#2c2f30] text-right outline-none pl-5"
                  onWheel={(e) => {
                    e.preventDefault();
                    handleScroll(player.user_id, e.deltaY < 0 ? 1 : -1);
                  }}
                />
              </div>
              <button 
                onClick={() => handleScroll(player.user_id, 1)}
                className="text-white bg-[#006855] border-none font-bold cursor-pointer w-8 h-8 p-0 m-0 text-[18px] flex items-center justify-center rounded-lg"
              >
                +
              </button>
            </div>
          </div>
        );
      })}

      {!showAll && allPlayers.length > visiblePlayers.length && (
        <button 
          onClick={() => setShowAll(true)}
          className="w-full text-[#595c5d] border border-[#e5e9ea] border-dashed rounded-[28px] py-3.5 px-6 text-sm font-bold cursor-pointer bg-transparent mt-2"
        >
          + SHOW ALL PLAYERS
        </button>
      )}

      {/* Spacer for bottom bar */}
      <div className="h-40" />

      <div className="fixed w-full max-w-[400px] left-1/2 -translate-x-1/2 px-4 py-3 bg-transparent z-10" style={{ bottom: 'calc(5rem + env(safe-area-inset-bottom))' }}>
        <div className="max-w-[400px] mx-auto flex flex-col items-center gap-3">
          <div className="flex justify-between items-center w-full px-1">
            <span className="text-[11px] font-bold uppercase tracking-wider text-[#595c5d]">Remaining to Distribute</span>
            <span className={`${remainingCash < 0 ? 'text-[#b31b25]' : 'text-[#006855]'} text-base font-extrabold`}>{formatCurrency(remainingCash)}</span>
          </div>
          <button
            onClick={handleNext}
            disabled={isSyncing || !isCashValid}
            style={{
              width: '70%',
              height: 44,
              background: 'linear-gradient(135deg, #008a6d, #006855)',
              color: 'white',
              border: 'none',
              borderRadius: 22,
              fontWeight: 'bold',
              textTransform: 'uppercase',
              fontSize: 13,
              cursor: (isSyncing || !isCashValid) ? 'not-allowed' : 'pointer',
              boxShadow: '0 4px 10px rgba(0,104,85,0.3)',
              opacity: (isSyncing || !isCashValid) ? 0.3 : 1,
            }}
          >
            {isSyncing ? 'SYNCING...' : (isCashValid ? 'CALCULATE' : 'DISCREPANCY')}
          </button>
          {isAdmin && (
            <button
              onClick={onBack}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#8a8d8e',
                fontSize: 11,
                fontWeight: 400,
                textDecoration: 'underline',
                cursor: 'pointer',
                paddingBottom: 8,
              }}
            >
              ← Back to Chip Counts
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
